import classNames from "classnames";
import { EDA } from "motw_core/Dreams/EDreamAttribute";
import IDream from "motw_core/Entity/IDream";
import * as React from "react";
import Cell from "source/app/presentation/MainPanel/Dreams/Cell/Cell";
import { DreamType } from "source/app/presentation/MainPanel/Dreams/DreamEditor/DreamType";
import { LaunchDreamEditor } from "source/app/presentation/MainPanel/Dreams/DreamEditor/Launch/Launch";
import Hyperlink from "source/app/presentation/Shared/Link/Hyperlink";
import { StyleClassAuto } from "source/app/presentation/Shared/Styles/Auto/StyleClassAuto";
import { StyleClass } from "source/app/presentation/Shared/Styles/CSS";
import { GetDreamAttributePrefix } from "../DreamAttributeIcon/AttributeMap/AttributeMap";
import { GetRenderedWord } from "./GetRenderedWord";

export function DreamName({
    dream,
    sortedData,
    isMasterDreamWithChildren,
    showTooltipOnlyWhenTruncated = false,
    isExpanded = false,
    collapsedLineCount = 1,
    onOverflowChange,
}: {
    dream: IDream;
    sortedData: IDream[];
    isMasterDreamWithChildren: boolean;
    showTooltipOnlyWhenTruncated?: boolean;
    isExpanded?: boolean;
    collapsedLineCount?: number;
    onOverflowChange?: (isOverflowing: boolean) => void;
}) {
    const textRef = React.useRef<HTMLSpanElement>(null);
    const [isOverflowing, setIsOverflowing] = React.useState(false);

    const parentDreamId = dream.linkid;
    const parentDream = sortedData.find((dream) => dream.id === parentDreamId);

    const isDirectionDreamStartingWithPlus =
        dream.attributes.includes(EDA[EDA.direction]) && dream.name.startsWith(`+`);
    const dreamPrefix =
        dream.name[0] === `_` || isDirectionDreamStartingWithPlus
            ? ``
            : GetDreamAttributePrefix(dream.attributes);
    const fullDreamText = `${dreamPrefix}${dream.name}`;
    const tooltipText =
        parentDream !== undefined
            ? `${fullDreamText} | part of dream '${parentDream.name}'`
            : fullDreamText;
    const resolvedTooltipText =
        showTooltipOnlyWhenTruncated && !isOverflowing ? undefined : tooltipText;

    React.useEffect(() => {
        const updateOverflowState = () => {
            const element = textRef.current;

            if (!element || isExpanded) {
                return;
            }

            const isCurrentlyOverflowing =
                collapsedLineCount > 1
                    ? element.scrollHeight > element.clientHeight
                    : element.scrollWidth > element.clientWidth;

            setIsOverflowing(isCurrentlyOverflowing);

            if (!isExpanded) {
                onOverflowChange?.(isCurrentlyOverflowing);
            }
        };

        updateOverflowState();
        window.addEventListener(`resize`, updateOverflowState);
        return () => window.removeEventListener(`resize`, updateOverflowState);
    }, [
        fullDreamText,
        showTooltipOnlyWhenTruncated,
        isMasterDreamWithChildren,
        isExpanded,
        collapsedLineCount,
        onOverflowChange,
    ]);

    const hasWaypointAttribute = dream.attributes.includes(EDA[EDA.waypoint]);
    const shouldClampMultipleLines =
        collapsedLineCount > 1 && !isExpanded;
    return (
        <Cell
            id="DreamName"
            style={
                showTooltipOnlyWhenTruncated
                    ? {
                          display: `inline-block`,
                          maxWidth: `100%`,
                          overflow: isExpanded ? `visible` : `hidden`,
                          verticalAlign: `bottom`,
                      }
                    : undefined
            }
        >
            <Hyperlink
                whenClicked={() =>
                    LaunchDreamEditor(DreamType.Existing, dream, sortedData)
                }
            >
                <span
                    ref={textRef}
                    data-tip={resolvedTooltipText}
                    title={isOverflowing ? fullDreamText : undefined}
                    style={
                        showTooltipOnlyWhenTruncated
                            ? {
                                  display: shouldClampMultipleLines
                                      ? `-webkit-box`
                                      : `inline-block`,
                                  maxWidth: `100%`,
                                  overflow: isExpanded ? `visible` : `hidden`,
                                  textOverflow: isExpanded
                                      ? `clip`
                                      : `ellipsis`,
                                  whiteSpace:
                                      isExpanded || shouldClampMultipleLines
                                          ? `normal`
                                          : `nowrap`,
                                  WebkitBoxOrient: shouldClampMultipleLines
                                      ? `vertical`
                                      : undefined,
                                  WebkitLineClamp: shouldClampMultipleLines
                                      ? collapsedLineCount
                                      : undefined,
                                  verticalAlign: `bottom`,
                              }
                            : undefined
                    }
                    className={classNames({
                        [masterDreamWithChildrenStyleClass]:
                            isMasterDreamWithChildren,
                        [isResonatingStyleClass]: dream.isresonating,
                        [waypointStyleClass]: hasWaypointAttribute,
                        [strikethroughStyleClass]:
                            hasWaypointAttribute && dream.difficulty > 6,
                    })}
                >
                    {dreamPrefix}
                    <WithWordRendering {...{ dreamName: dream.name }} />
                </span>
            </Hyperlink>
        </Cell>
    );
}

const waypointStyleClass = StyleClassAuto({ color: `yellowgreen` });

const strikethroughStyleClass = StyleClassAuto({
    textDecoration: `line-through`,
});

const masterDreamWithChildrenStyleClass = StyleClass(
    `dreamName_masterDreamParent`,
    {
        border: "1px dashed rgb(153 153 153 / 65%)",
        padding: "1px 7px",
        borderRadius: "4px",
    }
);

export const isResonatingStyleClass = StyleClass(`dreamName_isResonating`, {
    textShadow: `0px 0px 10px gold`,
    color: `#fbf5d5`,
});

function WithWordRendering({ dreamName }: { dreamName: string }) {
    const words = SplitSentence(dreamName);

    return (
        <span>
            {words.map((word, index) => (
                <GetRenderedWord
                    key={`${word}_${index}`}
                    {...{ word, isLastWord: index === words.length - 1 }}
                />
            ))}
        </span>
    );
}

function SplitSentence(sentence: string): string[] {
    return sentence.match(/\[[^\]]+\]|\S+/g) || [];
}
